import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface ManualMeasurementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  tradeId: string;
  tradeCode: string;
  tradeName: string;
  orgId: string;
  onSuccess: () => void;
}

export function ManualMeasurementDialog({
  open,
  onOpenChange,
  jobId,
  tradeId,
  tradeCode,
  tradeName,
  orgId,
  onSuccess,
}: ManualMeasurementDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    total_area: "",
    linear_feet: "",
    quantity: "",
    notes: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: userData } = await supabase.auth.getUser();
      
      const { error } = await supabase.from("measurements_generic").insert({
        job_id: jobId,
        job_trade_id: tradeId,
        org_id: orgId,
        trade_code: tradeCode,
        total_area: formData.total_area ? parseFloat(formData.total_area) : null,
        linear_feet: formData.linear_feet ? parseFloat(formData.linear_feet) : null,
        quantity: formData.quantity ? parseInt(formData.quantity) : null,
        notes: formData.notes || null,
        created_by: userData.user?.id,
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: `Measurements for ${tradeName} saved successfully`,
      });

      setFormData({ total_area: "", linear_feet: "", quantity: "", notes: "" });
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error("Error saving measurements:", error);
      toast({
        title: "Error",
        description: "Failed to save measurements",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Enter {tradeName} Measurements</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="total_area">Total Area (sq ft)</Label>
            <Input
              id="total_area"
              type="number"
              step="0.01"
              placeholder="e.g., 1200"
              value={formData.total_area}
              onChange={(e) => setFormData({ ...formData, total_area: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="linear_feet">Linear Feet (optional)</Label>
            <Input
              id="linear_feet"
              type="number"
              step="0.01"
              placeholder="e.g., 150"
              value={formData.linear_feet}
              onChange={(e) => setFormData({ ...formData, linear_feet: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="quantity">Quantity (optional)</Label>
            <Input
              id="quantity"
              type="number"
              placeholder="e.g., 10"
              value={formData.quantity}
              onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Add any additional details..."
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
            />
          </div>

          <div className="flex gap-3 justify-end pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Measurements
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
